import {get, defer} from '@servicenow/ui-internal';

const ENTER = 'enter';
const CHANGE = 'change';
const EXIT = 'exit';
const ABANDONED = 'abandoned';

export const GRAPHQL_SUBSCRIPTION_STARTED = 'GRAPHQL_SUBSCRIPTION_STARTED';
export const GRAPHQL_SUBSCRIPTION_SUCCEEDED = 'GRAPHQL_SUBSCRIPTION_SUCCEEDED';
export const GRAPHQL_SUBSCRIPTION_FAILED = 'GRAPHQL_SUBSCRIPTION_FAILED';
export const GRAPHQL_UNSUBSCRIBE_SUCCEEDED = 'GRAPHQL_UNSUBSCRIBE_SUCCEEDED';
export const GRAPHQL_SUBSCRIPTION_ITEM_ENTERED = 'GRAPHQL_SUBSCRIPTION_ITEM_ENTERED';
export const GRAPHQL_SUBSCRIPTION_ITEM_CHANGED = 'GRAPHQL_SUBSCRIPTION_ITEM_CHANGED';
export const GRAPHQL_SUBSCRIPTION_ITEM_EXITED = 'GRAPHQL_SUBSCRIPTION_ITEM_EXITED';
export const GRAPHQL_SUBSCRIPTION_CONDITION_ABANDONED = 'GRAPHQL_SUBSCRIPTION_CONDITION_ABANDONED';

function createSubscriptionCallback(dispatch, successActionType, errorActionType, actionMetaData) {
	// use setTimeout because of an AMB client subscription timing issue
	return response =>
		defer(() => {
			if (response.successful) {
				dispatch(successActionType, {response}, actionMetaData);
			} else {
				const {error} = response;
				dispatch(errorActionType, error, actionMetaData, true);
			}
		});
}

/**
 * Manges AMB subscription
 *
 * State should follow:
 * channelId has changed, always unsubscribe first
 * isSubscribed: true, shouldSubscribe: true    = Do nothing
 * isSubscribed: true, shouldSubscribe: false   = unsubscribe
 * isSubscribed: false, shouldSubscribe: true   = subscribe
 * isSubscribed: false, shouldSubscribe: false  = Do nothing
 *
 * @private
 * @param config
 */
export default function handleAMBSubscription(config) {
	const {
		shouldSubscribe,
		ambClient,
		ambChannelRegistry,
		options,
		actionMetaData,
		registryId,
		channelId,
		serializedSubscription: serializedGraphQLSubscription,
		dispatch
	} = config;

	const {
		subscriptionStartedActionType = GRAPHQL_SUBSCRIPTION_STARTED,
		subscriptionSuccessActionType = GRAPHQL_SUBSCRIPTION_SUCCEEDED,
		subscriptionFailedActionType = GRAPHQL_SUBSCRIPTION_FAILED,
		unsubscribeSuccessActionType = GRAPHQL_UNSUBSCRIBE_SUCCEEDED,
		itemEnteredActionType = GRAPHQL_SUBSCRIPTION_ITEM_ENTERED,
		itemChangedActionType = GRAPHQL_SUBSCRIPTION_ITEM_CHANGED,
		itemExitedActionType = GRAPHQL_SUBSCRIPTION_ITEM_EXITED,
		conditionAbandonedActionType = GRAPHQL_SUBSCRIPTION_CONDITION_ABANDONED
	} = options;

	const subscriptionCallback = createSubscriptionCallback(
		dispatch,
		subscriptionSuccessActionType,
		subscriptionFailedActionType,
		actionMetaData
	);

	if (!ambChannelRegistry.has(registryId)) {
		ambChannelRegistry.set(
			registryId,
			ambClient.getChannel(channelId, {
				subscriptionCallback,
				serializedGraphQLSubscription
			})
		);
	}

	let channelListener = ambChannelRegistry.get(registryId);
	const channelIdChanged = channelId !== channelListener.getName();

	if (channelIdChanged) {
		channelListener.unsubscribe();
		ambChannelRegistry.set(
			registryId,
			ambClient.getChannel(channelId, {
				subscriptionCallback,
				serializedGraphQLSubscription
			})
		);
		channelListener = ambChannelRegistry.get(registryId);
	}

	const isSubscribed = channelListener.getCallback() !== undefined;
	if (!isSubscribed && shouldSubscribe) {
		dispatch(subscriptionStartedActionType, {}, actionMetaData);
		channelListener.subscribe(message => {
			const fields = get(message, 'data.data.GlideRecord_Subscription', {});
			for (const key in fields) {
				if (key === '__typename') return;

				const state = get(fields[key], '_subscription.subscription_state');
				switch (state) {
					case CHANGE:
						dispatch(itemChangedActionType, message.data.data, actionMetaData);
						break;

					case ENTER:
						dispatch(itemEnteredActionType, message.data.data, actionMetaData);
						break;

					case EXIT:
						dispatch(itemExitedActionType, message.data.data, actionMetaData);
						break;

					case ABANDONED:
						dispatch(conditionAbandonedActionType, message.data.data, actionMetaData);
						break;
				}
			}
		});
	} else if (isSubscribed && !shouldSubscribe) {
		channelListener.unsubscribe();
		ambChannelRegistry.delete(registryId);
		dispatch(unsubscribeSuccessActionType, {}, actionMetaData);
	}
}
